import { Link } from 'react-router-dom';
import { can } from '../auth/store';
import { useComplianceRoster } from '../api/queries/compliance';
import { ApiError } from '../api/client';
import { StatusBadge, Table } from '../components/ui';
import type { RosterCheck, RosterJurisdictionEntry } from '@dsr/shared-types';

// Roster compliance: active personnel checked against each jurisdiction target's
// employee-level requirements (employee_registration, firearm_endorsement,
// training_cert, etc.). Driven by GET /api/compliance/roster. Gated on
// credential.read because it surfaces per-person credential status.
//
// Badge mapping (uses existing app.css status palette):
//   met            -> badge-active   (green)
//   expiring_soon  -> badge-pending  (amber)
//   missing        -> badge-expired  (red)
//   manual_review  -> badge-new      (signal/blue)

const STATUS_LABEL: Record<RosterCheck['status'], string> = {
  met: 'Met',
  expiring_soon: 'Expiring ≤90d',
  missing: 'Missing',
  manual_review: 'Manual review',
};

// Status → the existing app.css badge status suffix (StatusBadge emits
// `badge badge-${status}`). Keeps the met=green / expiring=amber /
// missing=red / manual_review=blue palette unchanged.
const STATUS_BADGE_SUFFIX: Record<RosterCheck['status'], string> = {
  met: 'active',
  expiring_soon: 'pending',
  missing: 'expired',
  manual_review: 'new',
};

function CheckChip({ check }: { check: RosterCheck }): JSX.Element {
  const label =
    check.status === 'expiring_soon' && check.expires_at != null
      ? `${STATUS_LABEL[check.status]} (${new Date(check.expires_at).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })})`
      : STATUS_LABEL[check.status];
  return <StatusBadge status={STATUS_BADGE_SUFFIX[check.status]}>{label}</StatusBadge>;
}

function JurisdictionSection({ entry }: { entry: RosterJurisdictionEntry }): JSX.Element {
  const autoReqs = entry.requirements.filter((r) => r.auto_scored);
  const manualReqs = entry.requirements.filter((r) => !r.auto_scored);

  return (
    <section className="dashboard-card roster-section">
      <h2 className="card-title">
        {entry.jurisdiction.name}{' '}
        <span className="page-sub" style={{ fontWeight: 400 }}>
          — {entry.service_line.replace(/_/g, ' ')} ({entry.target_status})
        </span>
      </h2>
      <p className="page-sub">
        <StatusBadge status="active">{entry.counts.met} met</StatusBadge>{' '}
        <StatusBadge status="pending">{entry.counts.expiring_soon} expiring</StatusBadge>{' '}
        <StatusBadge status="expired">{entry.counts.missing} missing</StatusBadge>{' '}
        <StatusBadge status="new">{entry.counts.manual_review} manual review</StatusBadge>
      </p>

      {entry.requirements.length === 0 ? (
        <p className="page-state">
          No employee-level requirements cataloged for this target.
        </p>
      ) : entry.personnel.length === 0 ? (
        <p className="page-state">
          No personnel on active assignments in{' '}
          {entry.state_code != null ? entry.state_code : 'this state'}.
        </p>
      ) : (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Person</Table.HeaderCell>
              {autoReqs.map((r) => (
                <Table.HeaderCell key={r.requirement_id} title={r.summary}>
                  {r.kind.replace(/_/g, ' ')}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {entry.personnel.map((p) => (
              <Table.Row key={p.personnel_id}>
                <Table.Cell tone="strong">
                  {p.full_name}
                  {p.employee_no != null ? (
                    <span className="page-sub"> {p.employee_no}</span>
                  ) : null}
                </Table.Cell>
                {autoReqs.map((r) => {
                  const check = p.checks.find((c) => c.requirement_id === r.requirement_id);
                  return (
                    <Table.Cell key={r.requirement_id}>
                      {check != null ? <CheckChip check={check} /> : '—'}
                    </Table.Cell>
                  );
                })}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {manualReqs.length > 0 && (
        <>
          <h3 className="card-title" style={{ marginTop: 'var(--space-4)' }}>
            Manual review requirements
          </h3>
          <ul>
            {manualReqs.map((r) => (
              <li key={r.requirement_id}>{r.summary}</li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

export function RosterCompliancePage(): JSX.Element {
  const canView = can('credential.read');
  const { data, isLoading, isError, error } = useComplianceRoster(canView);

  if (!canView) {
    return (
      <div className="page roster-compliance-page">
        <div className="page-state page-error" role="alert">
          Requires <code>credential.read</code> permission to view roster compliance.
        </div>
      </div>
    );
  }

  return (
    <div className="page roster-compliance-page">
      <header className="page-header">
        <div>
          <h1>Roster Compliance</h1>
          <p className="page-sub">
            Personnel on active assignments, checked against each target state's
            employee-level requirements.{' '}
            <Link to="/compliance">Back to dashboard</Link>
          </p>
        </div>
      </header>

      {isLoading && <div className="page-state">Loading roster compliance…</div>}
      {isError && (
        <div className="page-state page-error" role="alert">
          {error instanceof ApiError ? error.message : 'Failed to load roster compliance.'}
        </div>
      )}

      {!isLoading && !isError && data != null && (
        <>
          {data.unmapped_sites.length > 0 && (
            <div className="page-state page-error" role="alert">
              {data.unmapped_sites.length} site(s) with active assignments have no state set
              and are invisible to this view:{' '}
              {data.unmapped_sites.map((s) => s.name).join(', ')}.{' '}
              Set each site's State on its detail page.
            </div>
          )}

          {data.entries.length === 0 ? (
            <div className="page-state">
              No active jurisdiction targets. Add targets on the{' '}
              <Link to="/compliance">compliance dashboard</Link>.
            </div>
          ) : (
            <div className="gap-sections">
              {data.entries.map((e) => (
                <JurisdictionSection
                  key={`${e.jurisdiction.id}-${e.service_line}`}
                  entry={e}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
